import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { employeeAPI, wellbeingAPI, analyticsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Slider } from '../../components/ui/slider';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Progress } from '../../components/ui/progress';
import {
    Heart,
    Brain,
    Sparkles,
    TrendingUp,
    Zap,
    Target,
    ChevronRight,
    Lightbulb
} from 'lucide-react';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer
} from 'recharts';
import { getMoodEmoji, getMoodLabel, formatDate } from '../../lib/utils';

const MoodSelector = ({ value, onChange }) => {
    const moods = [1, 2, 3, 4, 5];
    
    return (
        <div className="flex justify-between gap-2">
            {moods.map((mood) => (
                <button
                    key={mood}
                    onClick={() => onChange(mood)}
                    className={`flex-1 py-4 rounded-xl transition-all duration-200 text-3xl ${
                        value === mood
                            ? 'bg-indigo-100 border-2 border-indigo-500 scale-105'
                            : 'bg-stone-50 border-2 border-transparent hover:bg-stone-100'
                    }`}
                    data-testid={`mood-btn-${mood}`}
                >
                    {getMoodEmoji(mood)}
                </button>
            ))}
        </div>
    );
};

const WellnessRecommendation = ({ rec }) => {
    const getIcon = (type) => {
        switch (type) {
            case 'mindfulness': return Sparkles;
            case 'exercise':
            case 'movement': return Zap;
            case 'stress': return Target;
            case 'mood': return Heart;
            case 'social': return TrendingUp;
            default: return Lightbulb;
        }
    };
    const Icon = getIcon(rec.type);

    return (
        <Card className="bento-card-interactive">
            <CardContent className="p-4 flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                    <h4 className="font-medium text-stone-900">{rec.title}</h4>
                    <p className="text-sm text-stone-500 mt-1">{rec.description}</p>
                </div>
            </CardContent>
        </Card>
    );
};

export default function EmployeeDashboard() {
    const { user } = useAuth();
    const [profile, setProfile] = useState(null);
    const [moodHistory, setMoodHistory] = useState([]);
    const [recommendations, setRecommendations] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Mood entry state
    const [mood, setMood] = useState(3);
    const [energy, setEnergy] = useState(3);
    const [stress, setStress] = useState(3);
    const [notes, setNotes] = useState('');
    const [submittingMood, setSubmittingMood] = useState(false);
    const [lastInsight, setLastInsight] = useState('');

    const fetchData = async () => {
        try {
            setLoading(true);
            const [profileRes, moodRes, recsRes] = await Promise.all([
                employeeAPI.getMyProfile(),
                wellbeingAPI.getMoodHistory(14),
                wellbeingAPI.getRecommendations()
            ]);
            setProfile(profileRes.data);
            setMoodHistory(moodRes.data || []);
            setRecommendations(recsRes.data?.recommendations || []);
            if (moodRes.data?.[0]?.ai_insight) {
                setLastInsight(moodRes.data[0].ai_insight);
            }
        } catch (error) {
            console.error('Failed to fetch data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleSubmitMood = async () => {
        try {
            setSubmittingMood(true);
            const response = await wellbeingAPI.logMood({
                mood,
                energy_level: energy,
                stress_level: stress,
                notes: notes || null
            });
            setLastInsight(response.data.ai_insight || '');
            setNotes('');
            await fetchData();
        } catch (error) {
            console.error('Failed to submit mood:', error);
        } finally {
            setSubmittingMood(false);
        }
    };

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-stone-500">Loading your dashboard...</p>
                </div>
            </div>
        );
    }

    const chartData = [...moodHistory].reverse().map(m => ({
        date: m.timestamp,
        mood: m.mood,
        energy: m.energy_level,
        stress: m.stress_level
    }));

    return (
        <div className="p-8" data-testid="employee-dashboard">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold font-heading text-stone-900">
                        Welcome back, {user?.name?.split(' ')[0] || 'there'}! ✨
                    </h1>
                    <p className="text-stone-500 mt-1">Track your well-being and discover personalized insights.</p>
                </div>

                {/* Main Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Mood Check-in Card */}
                    <Card className="lg:col-span-2 bento-card">
                        <CardHeader>
                            <CardTitle className="font-heading flex items-center gap-2">
                                <Heart className="w-5 h-5 text-coral-DEFAULT" />
                                Daily Check-in
                            </CardTitle>
                            <CardDescription>How are you feeling today?</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div>
                                <Label className="text-sm font-medium text-stone-700 mb-3 block">Mood</Label>
                                <MoodSelector value={mood} onChange={setMood} />
                                <p className="text-center text-sm text-stone-500 mt-2">
                                    {getMoodLabel(mood)}
                                </p>
                            </div>

                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <Label className="text-sm font-medium text-stone-700 mb-3 block">
                                        Energy Level: {energy}/5
                                    </Label>
                                    <Slider
                                        value={[energy]}
                                        onValueChange={([v]) => setEnergy(v)}
                                        min={1}
                                        max={5}
                                        step={1}
                                        className="w-full"
                                        data-testid="energy-slider"
                                    />
                                </div>
                                <div>
                                    <Label className="text-sm font-medium text-stone-700 mb-3 block">
                                        Stress Level: {stress}/5
                                    </Label>
                                    <Slider
                                        value={[stress]}
                                        onValueChange={([v]) => setStress(v)}
                                        min={1}
                                        max={5}
                                        step={1}
                                        className="w-full"
                                        data-testid="stress-slider"
                                    />
                                </div>
                            </div>

                            <div>
                                <Label className="text-sm font-medium text-stone-700 mb-2 block">
                                    Notes (optional)
                                </Label>
                                <Textarea
                                    placeholder="What's on your mind today?"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    className="resize-none rounded-xl"
                                    rows={3}
                                    data-testid="mood-notes-input"
                                />
                            </div>

                            <Button
                                onClick={handleSubmitMood}
                                disabled={submittingMood}
                                className="w-full h-12 rounded-xl bg-indigo-600 hover:bg-indigo-700"
                                data-testid="submit-mood-btn"
                            >
                                {submittingMood ? (
                                    <span className="flex items-center gap-2">
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        Saving...
                                    </span>
                                ) : (
                                    'Log Today\'s Mood'
                                )}
                            </Button>

                            {lastInsight && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="p-4 bg-indigo-50 rounded-xl border border-indigo-100"
                                >
                                    <div className="flex items-start gap-3">
                                        <Sparkles className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-sm font-medium text-indigo-900 mb-1">AI Insight</p>
                                            <p className="text-sm text-indigo-700">{lastInsight}</p>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Scores Card */}
                    <Card className="bento-card">
                        <CardHeader>
                            <CardTitle className="font-heading flex items-center gap-2">
                                <TrendingUp className="w-5 h-5 text-indigo-600" />
                                Your Scores
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div>
                                <div className="flex justify-between text-sm mb-2">
                                    <span className="text-stone-600">Sentiment</span>
                                    <span className="font-medium text-stone-900">
                                        {((profile?.sentiment_score || 0) * 100).toFixed(0)}%
                                    </span>
                                </div>
                                <Progress value={(profile?.sentiment_score || 0) * 100} className="h-2" />
                            </div>
                            <div>
                                <div className="flex justify-between text-sm mb-2">
                                    <span className="text-stone-600">Engagement</span>
                                    <span className="font-medium text-stone-900">
                                        {((profile?.engagement_score || 0) * 100).toFixed(0)}%
                                    </span>
                                </div>
                                <Progress value={(profile?.engagement_score || 0) * 100} className="h-2" />
                            </div>
                            <div>
                                <div className="flex justify-between text-sm mb-2">
                                    <span className="text-stone-600">Well-being</span>
                                    <span className="font-medium text-stone-900">
                                        {((profile?.wellbeing_score || 0) * 100).toFixed(0)}%
                                    </span>
                                </div>
                                <Progress value={(profile?.wellbeing_score || 0) * 100} className="h-2" />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Mood History Chart */}
                <Card className="mt-6 bento-card">
                    <CardHeader>
                        <CardTitle className="font-heading">Mood History</CardTitle>
                        <CardDescription>Your mood, energy, and stress levels over time</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="h-64">
                            {chartData.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={chartData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                                        <XAxis
                                            dataKey="date"
                                            tick={{ fontSize: 12, fill: '#78716c' }}
                                            tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                        />
                                        <YAxis
                                            domain={[1, 5]}
                                            tick={{ fontSize: 12, fill: '#78716c' }}
                                            ticks={[1, 2, 3, 4, 5]}
                                        />
                                        <Tooltip
                                            content={({ active, payload }) => {
                                                if (active && payload?.length) {
                                                    return (
                                                        <div className="glass p-3 rounded-xl text-sm">
                                                            <p className="font-medium text-stone-900 mb-1">
                                                                {formatDate(payload[0].payload.date)}
                                                            </p>
                                                            <p className="text-indigo-600">Mood: {payload[0].value}/5</p>
                                                            <p className="text-emerald-600">Energy: {payload[1]?.value}/5</p>
                                                            <p className="text-coral-DEFAULT">Stress: {payload[2]?.value}/5</p>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            }}
                                        />
                                        <Line type="monotone" dataKey="mood" stroke="#4F46E5" strokeWidth={2} dot={{ fill: '#4F46E5' }} />
                                        <Line type="monotone" dataKey="energy" stroke="#10B981" strokeWidth={2} dot={{ fill: '#10B981' }} />
                                        <Line type="monotone" dataKey="stress" stroke="#F47266" strokeWidth={2} dot={{ fill: '#F47266' }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="flex items-center justify-center h-full">
                                    <p className="text-stone-500">Start logging your mood to see your history!</p>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Recommendations */}
                <div className="mt-6">
                    <h2 className="text-xl font-bold font-heading text-stone-900 mb-4">
                        Wellness Recommendations
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {recommendations.map((rec, index) => (
                            <WellnessRecommendation key={index} rec={rec} />
                        ))}
                    </div>
                </div>
            </motion.div>
        </div>
    );
}
